/**
 * File: contextService.ts
 * Description: Service for capturing enhanced context for feedback
 * Author: System
 * Created: 2025-10-06
 * Last Modified: 2025-10-06
 */

import { contextManager } from '../../../services/contextManager';
import { streamingChatService } from '../../../services/streamingChatService';
import { logger } from '../../../utils/logger';

import type { AssistantRuntimeContext } from './types';

export interface EnhancedContext {
  chat_history: Array<{
    role: string;
    content: string;
    timestamp: string;
    id: string;
  }>;
  tool_calls: any[];
  context_data: any[];
  session_id: string | null;
  thread_id: string | null;
  runtime_info: {
    messages_count: number;
    tool_calls_count: number;
    context_keys_count: number;
    has_assistant_runtime: boolean;
    capture_error?: string;
  };
}

export const captureEnhancedContext = (
  assistantRuntimeContext?: AssistantRuntimeContext,
): EnhancedContext => {
  try {
    // Get basic context that's always available
    const currentContext = contextManager.getContext();
    const sessionId = streamingChatService.getCurrentSessionId();

    // Use assistant runtime context if provided, otherwise use empty arrays
    const chatHistory = assistantRuntimeContext?.chatHistory || [];
    const toolCalls = assistantRuntimeContext?.toolCalls || [];
    const hasAssistantRuntime = !!assistantRuntimeContext;

    return {
      chat_history: chatHistory.map((msg: any) => ({
        role: msg.role,
        content:
          typeof msg.content === 'string'
            ? msg.content
            : msg.content
              ?.map((part: any) =>
                part.type === 'text' ? part.text : `[${part.type}]`,
              )
              .join('') || '',
        timestamp: msg.createdAt || new Date().toISOString(),
        id: msg.id,
      })),
      tool_calls: toolCalls,
      context_data: currentContext,
      session_id: sessionId,
      thread_id: assistantRuntimeContext?.threadId || null,
      runtime_info: {
        messages_count:
          assistantRuntimeContext?.messagesCount || chatHistory.length,
        tool_calls_count:
          assistantRuntimeContext?.toolCallsCount || toolCalls.length,
        context_keys_count: currentContext.length,
        has_assistant_runtime: hasAssistantRuntime,
      },
    };
  } catch (error) {
    logger.warn('Failed to capture enhanced context:', error);
    return {
      chat_history: [],
      tool_calls: [],
      context_data: [],
      session_id: null,
      thread_id: null,
      runtime_info: {
        messages_count: 0,
        tool_calls_count: 0,
        context_keys_count: 0,
        capture_error: error instanceof Error ? error.message : 'Unknown error',
        has_assistant_runtime: false,
      },
    };
  }
};